import React, { Children, useEffect } from 'react';
import { ButtonType } from '../../../types/ButtonType';
import { CollapsibleContent } from '../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay';
import useCollapsibleOverlay from '../../../hooks/useCollapsibleOverlay';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { Icons } from '../../../components/atoms/SVG';
import { BtnModifier } from '../../../components/molecules/Btn';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { Button } from '../../../components/molecules/Btn/Button';

type ContextMenuProps = {
  tooltipText: string;
  children: any;
  tooltipPlacement?: TooltipPlacement;
  centered?: boolean;
};

export function ContextMenu({
  tooltipText,
  children,
  centered,
  tooltipPlacement = TooltipPlacement.RIGHT,
}: ContextMenuProps) {
  const { collapsibleOpen, collapsibleProps, toggleProps, popperInstance } = useCollapsibleOverlay(
    centered ? 'bottom' : 'bottom-end'
  );
  const buttonId = useHtmlValidId();

  /**
   * Trigger update function of popper manually.
   * Context menu often changes position in DOM
   *  -> popper cant know when this happens if children are rendered at the end of the DOM.
   *  -> https://popper.js.org/docs/v2/modifiers/event-listeners/
   *
   * No Deps array as we want to recalculate everytime the context menu renders,
   * but we restrict the update only on open popper instances
   */
  useEffect(() => {
    if (collapsibleOpen) {
      popperInstance?.update();
    }
  });

  return (
    <div className="context-menu">
      <Button
        {...toggleProps}
        id={buttonId}
        tooltipPlacement={tooltipPlacement}
        type={ButtonType.BUTTON}
        title={tooltipText}
        hideTooltipFromScreenReader
        modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
      >
        <Button.Icon text={tooltipText} svgSpriteId={collapsibleOpen ? Icons.DECLINE_CROSS : Icons.MORE_MENU} />
      </Button>
      <CollapsibleContent {...collapsibleProps}>
        {/* ul is never empty because ContextMenu always has children,
            but it should not have empty <li> as well. */}
        <ul className="context-menu__button-list" aria-labelledby={buttonId}>
          {Children.map(children, child =>
            child ? <li className="context-menu__button-list-item">{child}</li> : null
          )}
        </ul>
      </CollapsibleContent>
    </div>
  );
}
